"use client";

import { useSingleton } from "@rosette/web_utils/src/useSingleton.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { matomo } from "@/components/Matomo.js";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { SelfPlayComputerGameSource } from "@rosette/ts/src/business/game/controller/source/SelfPlayComputerGameSource.js";
import { SimpleEngineProvider } from "@rosette/ts/src/royalur/engine/simple/SimpleEngineProvider.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { DeviceInfoProvider } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { AssetProvider } from "@/components/assets/AssetContext.js";
import { GameUIContext, useGameController } from "@/components/game/GameUIContext.js";
import React, { useEffect } from "react";
import BoardUI from "@/components/game/board/BoardUI.js";
import { useCreateSharedRendererState } from "@rosette/web_components/src/game/SharedRendererState.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import type { GameController } from "@rosette/ts/src/business/game/controller/GameController.js";
import { cn } from "@rosette/ts/src/util/cn.js";


interface GameControllerCaptureProps {
    capture: (gameController: GameController) => (() => void);
}


function GameControllerCapture({ capture }: GameControllerCaptureProps) {
    const gameController = useGameController();
    useEffect(() => {
        return capture(gameController);
    }, [capture, gameController]);
    return (<></>);
}


interface RandomGameProps {
    className?: string;
    orientation?: "horizontal" | "vertical";
    inArticle?: boolean;
}


export function RandomGame({ className, orientation, inArticle }: RandomGameProps) {
    const clientControls = useSingleton<GameClientControls>(() => {
        const missing = () => {
            throw new Error("This function is missing!");
        };
        return {
            startGame: missing,
            startingGame: false,
            startGameError: null,
            clearError: missing,
            resignGame: missing,
            abortGame: missing,
            notify: missing,
        };
    });

    const user = useOptionalUser();
    const { preferences } = useGamePreferences(user);
    const gameAPI = useGameAPI();
    const gameSource = useSingleton(() => {
        const settings = new KnownLobbySettings(
            GameMode.COMPUTER,
            Ruleset.FINKEL,
            Ruleset.FINKEL.getSettings(),
            BotType.EASY,
        );
        const rules = new SimpleEngineProvider().create(settings.getGameSettings());
        return new SelfPlayComputerGameSource(
            clientControls,
            preferences,
            matomo,
            gameAPI,
            settings,
            rules,
            settings.getBotType(),
            settings.getBotType().createAgent(rules),
        );
    });

    const sharedRendererState = useCreateSharedRendererState();

    return (
        <DeviceInfoProvider>
            <AssetProvider>
                <GameUIContext gameSource={gameSource} clientControls={clientControls}>
                    <GameControllerCapture
                        capture={gameController => gameSource.linkGameController(gameController)} />

                    <div className={cn(
                        "flex w-full",
                        inArticle ? "min-h-40 md:min-h-72" : null,
                        className ?? null,
                    )}>
                        <BoardUI
                            vertical={orientation === "vertical"}
                            horizontal={!orientation || orientation === "horizontal"}
                            isMobile={true}
                            isPathActive={false}
                            sharedRendererState={sharedRendererState} />
                    </div>
                </GameUIContext>
            </AssetProvider>
        </DeviceInfoProvider>
    );
}
